/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Component voor de chat bij een model (tot golf 4f een Custom Element): de bel met ongelezen-markering
 * en het chatvenster. Vervangt de AngularJS #chat-markup in canvas.html.
 *
 * Sinds golf a1 leven de chatregels, het chatrecht en het transport in de `ChatService`; dit
 * eiland verzorgt alleen nog de weergave en de bediening. Sinds golf a3 komt ook het laatste
 * gegeven uit ng21: of het model al opgeslagen is — bij een vers, nog niet bewaard model is er
 * geen chat — leest het eiland uit de `OpslagService`.
 *
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 21-07-2026
 */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    signal,
    ViewChild
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateModule, TranslatePipe, TranslateService} from '@ngx-translate/core';
import {DateTime} from 'luxon';
import {BaseIslandComponent} from '../base-island.component';
import {VideoStatusService} from '../../services/video-status.service';
import {ChatService, type ChatRegel} from '../../services/chat.service';
import {OpslagService} from '../../app/model/opslag.service';

/** Een chatregel met de afgeleide, in de gebruikerstaal geformuleerde tijdsaanduiding. */
interface ChatRegelWeergave extends ChatRegel {
    tijd: string;
}

@Component({
    selector: 'dl-chat',
    standalone: true,
    templateUrl: './dl-chat.component.html',
    styleUrls: ['./dl-chat.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, TranslatePipe],
    host: {
        '[class.dl-chat--zichtbaar]': 'zichtbaar()'
    }
})
export class DlChatComponent extends BaseIslandComponent {

    /**
     * De vertaalservice, voor de plaatsing van de teksten en de taal van de tijdsaanduiding.
     */
    private readonly translateService = inject(TranslateService);

    /**
     * De gedeelde videostatus: de chat wijkt voor een spelende instructievideo.
     */
    private readonly videoStatus = inject(VideoStatusService);

    /**
     * De chat zelf: de regels, het chatrecht en het versturen.
     */
    private readonly chat = inject(ChatService);

    /**
     * De opslagketen: bij een nieuw, nog niet bewaard model is er geen chat.
     */
    private readonly opslag = inject(OpslagService);

    /**
     * Het scrollbare chatlog, voor het naar beneden scrollen bij nieuwe berichten.
     */
    @ViewChild('chatlog') private chatlog?: ElementRef<HTMLDivElement>;

    constructor() {
        super();
        this.translateService.onLangChange
            .pipe(takeUntilDestroyed())
            .subscribe(gebeurtenis => this._taal.set(gebeurtenis.lang));

        // Zolang de chat open staat de tijdsaanduidingen laten meelopen ("zojuist" wordt "1 minuut geleden")
        effect(opruimen => {
            if (!this._open()) {
                return;
            }
            const teller = setInterval(() => this._tik.update(tik => tik + 1), 30000);
            opruimen(() => clearInterval(teller));
        });

        // Een ander model: de chat gaat dicht en het invoerveld is leeg
        effect(() => {
            this.chat.sessieteller();
            this._open.set(false);
            this._tekst = '';
            this.cdr.markForCheck();
        });

        // Staat de chat open, dan zijn binnengekomen berichten meteen gezien
        effect(() => {
            const regels = this.chat.regels();
            if (this._open() && regels.length) {
                this.meldGelezen();
                this.scrollNaarBeneden();
            }
        });
    }

    // --- Interne state ---

    /** Open/dicht state van het chatvenster. */
    protected readonly _open = signal(false);

    /** De taal waarin de tijdsaanduiding geformuleerd wordt. */
    private readonly _taal = signal(this.translateService.currentLang);

    /** Loopt op zodra de tijdsaanduidingen herberekend moeten worden. */
    private readonly _tik = signal(0);

    /** De tekst in het invoerveld; blijft bewaard als de gebruiker de chat tussendoor sluit. */
    protected _tekst = '';

    /**
     * De chat is alleen te zien bij een opgeslagen model, met chatrecht en zonder video. Of het
     * model opgeslagen is komt sinds golf a3 uit de opslagketen.
     */
    protected readonly zichtbaar = computed(() => this.chat.magChat() && this.opslag.isOpgeslagen() && !this.videoStatus.speelt());

    /**
     * Mag er getypt en verstuurd worden? Het gesprek blijft leesbaar zonder verbinding, maar het
     * invoerveld verdwijnt: een bericht dat toch nergens aankomt hoort niet verstuurbaar te lijken.
     */
    protected readonly magVersturen = this.chat.magVersturen;

    /** Er zijn berichten die de gebruiker nog niet gezien heeft. */
    protected readonly heeftNieuw = this.chat.heeftNieuw;

    /** De chatregels met hun tijdsaanduiding in de taal van de interface. */
    protected readonly regelsWeergave = computed<ChatRegelWeergave[]>(() => {
        this._tik(); //afhankelijkheid: hierdoor lopen de tijdsaanduidingen mee
        const taal = this._taal();
        return this.chat.regels().map(regel => ({...regel, tijd: this.tijdsaanduiding(regel.moment, taal)}));
    });

    // --- Interactie ---

    /**
     * Opent of sluit het chatvenster. Bij openen zijn de aanwezige berichten gezien.
     */
    protected klikBel(): void {
        this._open.update(open => !open);
        if (this._open()) {
            this.meldGelezen();
            this.scrollNaarBeneden();
        }
    }

    /**
     * Houdt de ingetikte tekst bij.
     */
    protected onInvoer(gebeurtenis: Event): void {
        this._tekst = (gebeurtenis.target as HTMLTextAreaElement).value;
    }

    /**
     * Enter verstuurt het bericht in plaats van een nieuwe regel te maken.
     */
    protected onToets(gebeurtenis: KeyboardEvent): void {
        if (gebeurtenis.key === 'Enter') {
            gebeurtenis.preventDefault();
            this.verstuurTekst();
        }
    }

    /**
     * Verstuurt de ingetikte tekst en maakt het invoerveld leeg.
     */
    protected verstuurTekst(): void {
        if (!this._tekst.length) {
            return;
        }
        this.chat.verstuur(this._tekst);
        this._tekst = '';
        this.cdr.markForCheck();
    }

    // --- Hulpmethoden ---

    /**
     * Meldt de nog niet gemelde nieuwe berichten als gelezen.
     */
    private meldGelezen(): void {
        this.chat.meldGelezen(this.chat.regels().filter(regel => regel.nieuw).map(regel => regel.id));
    }

    /**
     * Scrolt het chatlog naar het laatste bericht, zodra het venster getekend is.
     */
    private scrollNaarBeneden(): void {
        setTimeout(() => {
            const element = this.chatlog?.nativeElement;
            if (element) {
                element.scrollTop = element.scrollHeight;
            }
        }, 50);
    }

    /**
     * Geeft het tijdstip als een relatieve aanduiding in de opgegeven taal. Een tijd in de toekomst —
     * mogelijk door een klokverschil tussen client en server — tonen we als "nu".
     */
    private tijdsaanduiding(moment: number, taal: string): string {
        const tijdstip = DateTime.fromMillis(moment).setLocale(taal);
        const nu = DateTime.now().setLocale(taal);
        return (tijdstip > nu ? nu : tijdstip).toRelative() || '';
    }
}
